import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Suspense, lazy } from "react";
import { AuthProvider } from "./context/auth-context";
import { SubscriptionProvider } from "./contexts/subscription-context";
import { RedirectAuth } from "@/components/auth/redirect-auth";
import { GoogleAnalytics } from "@/components/analytics/google-analytics";
import { ConsentBanner } from "@/components/consent/consent-banner";
import { FloatingConsentIcon } from "@/components/consent/floating-consent-icon";

// Lazy load components for better code splitting
const NotFound = lazy(() => import("@/pages/not-found"));
const LandingPage = lazy(() => import("@/pages/landing"));
const AdsLandingPage = lazy(() => import("@/pages/ads-landing"));
const Login = lazy(() => import("@/pages/auth/login"));
const Register = lazy(() => import("@/pages/auth/register"));
const ResetPassword = lazy(() => import("@/pages/auth/reset-password"));
const ConfirmPasswordReset = lazy(() => import("@/pages/auth/confirm-password-reset"));
const VerifyEmail = lazy(() => import("@/pages/auth/verify-email"));
const ConfirmEmailVerification = lazy(() => import("@/pages/auth/confirm-email-verification"));
const AuthAction = lazy(() => import("@/pages/auth/auth-action"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const ServersIndex = lazy(() => import("@/pages/servers/index"));
const ServerDetails = lazy(() => import("@/pages/servers/[id]/index"));
const NewServer = lazy(() => import("@/pages/servers/new"));
const Settings = lazy(() => import("@/pages/settings"));
const Support = lazy(() => import("@/pages/support"));
const Contact = lazy(() => import("@/pages/contact"));
const Activity = lazy(() => import("@/pages/activity"));
const About = lazy(() => import("@/pages/about"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy-policy"));
const TermsOfService = lazy(() => import("@/pages/terms-of-service"));
const CookiesPolicy = lazy(() => import("@/pages/cookies-policy"));
const FAQ = lazy(() => import("@/pages/faq"));
const BlogIndex = lazy(() => import("@/pages/blog/index"));
const BlogPost = lazy(() => import("@/pages/blog/[slug]"));
const Workbench = lazy(() => import("@/pages/workbench"));
const VmcIndex = lazy(() => import("@/pages/vmcp/index"));
const VmcNew = lazy(() => import("@/pages/vmcp/new"));
const VmcDetail = lazy(() => import("@/pages/vmcp/[id]/index"));

// Loading fallback
const Loading = () => (
  <div className="flex items-center justify-center h-screen w-screen bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

function Router() {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {/* Main Landing Page - No Auto Redirect */}
        <Route path="/">
          <LandingPage />
        </Route>
        
        {/* Google Ads Landing Page */}
        <Route path="/get-started" component={AdsLandingPage} />
        
        {/* In Action - Landing with Auto Demo */}
        <Route path="/in-action">
          <Redirect to="/?demo=open" />
        </Route>
        
        {/* Auth Routes */}
        <Route path="/auth/login" component={Login} />
        <Route path="/auth/register" component={Register} />
        <Route path="/auth/reset-password" component={ResetPassword} />
        <Route path="/auth/confirm-password-reset" component={ConfirmPasswordReset} />
        <Route path="/auth/verify-email" component={VerifyEmail} />
        <Route path="/auth/confirm-email-verification" component={ConfirmEmailVerification} />
        
        {/* Firebase Auth Action Handler */}
        <Route path="/__/auth/action" component={AuthAction} />
        
        {/* App Routes - Protected */}
        <Route path="/dashboard">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <Dashboard />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/servers">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <ServersIndex />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/workbench">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <Workbench />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/servers/new">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <NewServer />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/vmcp/new">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <VmcNew />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/vmcp">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <VmcIndex />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/vmcp/:id">
          {(params) => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <VmcDetail />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/servers/:id">
          {(params) => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <ServerDetails />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/settings">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <Settings />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/support">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <Support />
            </RedirectAuth>
          )}
        </Route>
        <Route path="/activity">
          {() => (
            <RedirectAuth redirectTo="/auth/login" whenAuthenticated={false}>
              <Activity />
            </RedirectAuth>
          )}
        </Route>

        {/* Legal Routes */}
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/cookies-policy" component={CookiesPolicy} />
        <Route path="/faq" component={FAQ} />
        <Route path="/contact" component={Contact} />
        <Route path="/about" component={About} />
        
        {/* Blog Routes */}
        <Route path="/blog" component={BlogIndex} />
        <Route path="/blog/:slug" component={BlogPost} />

        {/* Fallback to 404 */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <Router />
          <GoogleAnalytics />
          <ConsentBanner />
          <FloatingConsentIcon />
          <Toaster />
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
